import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { NotificationsProvider } from "@/context/NotificationsContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Dashboard from "@/pages/Dashboard";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Quotes from "@/pages/Quotes";
import QuoteEditor from "@/pages/QuoteEditor";
import Invoices from "@/pages/Invoices";
import InvoiceDetail from "@/pages/InvoiceDetail";
import Communications from "@/pages/Communications";
import Reports from "@/pages/Reports";
import Revenue from "@/pages/Revenue";
import Settings from "@/pages/Settings";
import Notifications from "@/pages/Notifications";
import Calculator from "@/pages/Calculator";
import PublicQuote from "@/pages/PublicQuote";

const NotFound = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="not-found">
    <div className="text-center max-w-md">
      <div className="text-7xl font-display font-extrabold text-slate-900">404</div>
      <div className="mt-2 font-display text-2xl font-bold text-slate-900">Page not found</div>
      <p className="mt-2 text-sm text-slate-500">That URL doesn't match anything in Quick Quotes.</p>
      <a href="/dashboard" className="mt-6 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-2.5 rounded-md" data-testid="not-found-home">
        Back to dashboard
      </a>
    </div>
  </div>
);

const RequireAccount = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

const RequireAuth = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <NotificationsProvider>
          <BrowserRouter>
            <Toaster position="top-right" richColors />
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/pricing" element={<RequireAuth><Pricing /></RequireAuth>} />
              <Route path="/payment/success" element={<RequireAuth><PaymentSuccess /></RequireAuth>} />
              <Route path="/quote/:qid" element={<PublicQuote />} />
              <Route path="/dashboard" element={<RequireAccount><Layout /></RequireAccount>}>
                <Route index element={<Dashboard />} />
                <Route path="customers" element={<Customers />} />
                <Route path="customers/:cid" element={<CustomerDetail />} />
                <Route path="quotes" element={<Quotes />} />
                <Route path="quotes/new" element={<QuoteEditor />} />
                <Route path="quotes/:qid" element={<QuoteEditor />} />
                <Route path="invoices" element={<Invoices />} />
                <Route path="invoices/:iid" element={<InvoiceDetail />} />
                <Route path="calculator" element={<Calculator />} />
                <Route path="communications" element={<Communications />} />
                <Route path="notifications" element={<Notifications />} />
                <Route path="reports" element={<Reports />} />
                <Route path="revenue" element={<Revenue />} />
                <Route path="settings" element={<Settings />} />
              </Route>
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </NotificationsProvider>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
